/**
 * Every string `/subscriptions` renders.
 *
 * The currency word appears nowhere here. It travels on the ledger row, written
 * from the `CURRENCY` setting when the record was created, and the amount tile
 * borrows it from the same place — so no surface in this repo holds a currency
 * literal and a settings change reaches both bands without a rebuild.
 *
 * Two lines carry facts the operator can act on rather than reasons the surface
 * is shaped the way it is: where a manual grant is counted, and where an
 * entitlement is granted or lifted. The ledger's pair is read **above** its
 * lookup bar — a caveat about what a page sums to is a precondition for reading
 * the table, and under a pager it reaches only whoever scrolled that far.
 */
export const copy = {
  crumb: 'تقارير الاشتراك',

  head: {
    title: 'تقارير الاشتراك',
    lede: 'من اشترك، وبأيّ خطّة، وبكم، ومتى — الأربعة في مكان واحد. الأرقام حال الخدمة عند لحظة فتح الصفحة.',
  },

  totals: {
    heading: 'الحصيلة',
    link: 'إدارة الخطط ←',
    caption: 'الإيراد المحصّل بحسب الخطّة',
    columns: {
      plan: 'الخطّة',
      share: 'الحصّة',
      revenue: 'الإيراد المحصّل',
    },
    total: 'المجموع',
    /** Why a manual grant is not a row in the plan table, and where its figure is. */
    grantNote:
      'المنح اليدوية خارج هذا الجدول: هي مصدر استحقاق لا خطّة مبيعة، فلا سعر لها ولا فترة، وعددها في بطاقة الأرقام أعلاه.',
  },

  /**
   * Tile wording. The wire carries a key and an integer; the label is the
   * console's, because the owning service keeps every shaping decision on the
   * reading side of the hop. A key with no entry renders as itself rather than
   * being dropped — a figure the operator cannot name is still a figure, and a
   * missing one is a hole.
   */
  tiles: {
    total_amount: 'الإيراد المحصّل',
    active_count: 'اشتراكات فاعلة',
    expired_count: 'اشتراكات منتهية',
    manual_grant_count: 'منح يدوية فاعلة',
  } as Readonly<Record<string, string>>,

  /**
   * The lookup bar's strings.
   *
   * **Neither vocabulary list here is a decision taken in this file.** The states
   * are the owning service's lifecycle and the sources are the members
   * `ENTITLEMENT_MODES` permits; both are listed as machine tokens and rendered
   * through the area's own resolvers, so a label is written in exactly one place
   * and a value the service adds degrades to itself rather than disappearing.
   *
   * The plan list is not here at all — plans are operator-defined rows read from
   * the money service, and a hard-coded plan name would be a second, stale copy
   * of a record the console can already read.
   */
  filters: {
    legend: 'ضيّق سجلّ الاشتراكات بالمشترك أو بالخطّة أو بالحالة أو بالمصدر.',

    term: {
      label: 'ابحث عن مشترك',
      placeholder: 'الاسم أو المعرّف أو رقم الهاتف',
      /** Why the term reaches the account register rather than the money service. */
      hint: 'يُطابَق البحث على حسابات المستخدمين، ثمّ يُعرض ما يملكه المطابِقون من اشتراكات ومنح.',
    },

    plan: { label: 'الخطّة', all: 'كلّ الخطط' },

    state: {
      label: 'الحالة',
      all: 'كلّ الحالات',
      values: ['ACTIVE', 'PENDING', 'EXPIRED', 'CANCELLED'],
    },

    source: {
      label: 'المصدر',
      all: 'كلّ المصادر',
      values: ['SUBSCRIPTION', 'MANUAL_GRANT'],
    },

    reset: 'امسح المرشّحات',
    searching: 'جارٍ البحث',

    /** What the term resolved to — the difference between «يملك لا شيء» and «لا أحد بهذا الاسم». */
    matchedAccounts: (count: string) => `طابَق البحث ${count} من الحسابات.`,
    noAccounts: 'لا حساب يطابق هذا البحث.',
    truncated: 'عُرض أوّل مئة حساب مطابِق فقط؛ ضيّق البحث لتشمل البقيّة.',
  },

  /**
   * The row action — `[x]` §20.25.
   *
   * **One ending on a sale, and one on a grant.** The second entry a sale used
   * to carry — «أوقف التجديد» — went with automatic renewal itself: nothing
   * renews, so leaving a subscription alone already gives the subscriber the
   * period they paid for, to its last day. A menu entry that promised to stop a
   * charge nobody makes would change no date while reading as though it did,
   * which is worse than its absence.
   */
  rowMenu: {
    label: (subscriber: string) => `أفعال سجلّ «${subscriber}»`,
    cancelNow: 'أنهِ الوصول الآن',
    revoke: 'ارفع المنحة',
  },

  /**
   * The confirmation — three sentences, and each one says something the row does
   * not.
   *
   * **When the access stops** is the first, and it is now the sentence that
   * carries the whole act: the operator is cutting a period short rather than
   * choosing between two dates. **That no money moves** is the answer to the
   * question an operator asks next and would otherwise ask support. **That
   * nothing is deleted** is what makes the act safe to take: the record stays in
   * this table, which is what keeps «who bought, and when» answerable about a
   * month that has closed.
   *
   * The heading names the account, per the console's confirmation discipline. A
   * generic «هل أنت متأكّد؟» cannot state a consequence, which is the entire
   * point of the step.
   */
  confirm: {
    cancelNow: {
      heading: (subscriber: string) => `إنهاء وصول «${subscriber}» الآن؟`,
      body: 'ينقطع الوصول فور التأكيد، ويخسر المشترك ما تبقّى من فترته المدفوعة. لا يُستردّ المبلغ المحصّل من هنا — الاسترداد يقع لدى مزوّد الدفع. ويبقى السجلّ في هذا الجدول: لا يُحذف شيء، وإنّما تُغلق مدّة الاستحقاق عند هذه اللحظة.',
      commit: 'أنهِ الوصول',
    },
    revoke: {
      heading: (subscriber: string) => `رفع منحة «${subscriber}»؟`,
      body: 'ينقطع الوصول الممنوح فور التأكيد. لا مبلغ في هذه المنحة يُستردّ، ويبقى سجلّها في هذا الجدول كما هو: تُغلق مدّتها ولا تُحذف.',
      commit: 'ارفع المنحة',
    },
  },

  /** Said once, after the write the service confirmed — never before it. */
  toast: {
    cancelledNow: 'انتهى الوصول.',
    revoked: 'رُفعت المنحة.',
  },

  ledger: {
    heading: 'أحدث الاشتراكات',
    caption: 'أحدث الاشتراكات والمنح: المشترك، والخطّة، والمصدر، والمبلغ، والتاريخ، والحالة',
    columns: {
      subscriber: 'المشترك',
      plan: 'الخطّة',
      source: 'المصدر',
      amount: 'المبلغ',
      date: 'التاريخ',
      state: 'الحالة',
      /** Visually hidden — a menu cell holds nothing a header can name, and an empty `<th>` has no accessible name at all. */
      actions: 'أفعال',
    },
    /** An em-dash is a punctuation mark to a screen reader; these are what it reads instead. */
    noPlan: 'لا خطّة',
    noAmount: 'لا مبلغ',

    /**
     * Why a page of this table does not add up to the band above it.
     *
     * The sentence used to read «فمجموعها ليس مجموع الحصيلة» — *their* sum —
     * because the table showed the newest rows and nothing else. Every row is
     * now reachable, so their sum **is** the total and only a single page's is
     * not. One word, and the note stays true rather than becoming the surface's
     * one false claim.
     *
     * Its second half used to send an operator to the account register **to
     * search** — which this surface could not do and now does. It sends them
     * there for what the register actually holds instead: the account itself,
     * with its roles, its blocks and its case history. A narrowed ledger also
     * changes the arithmetic, so the clause naming the total says which figure
     * moved.
     */
    pageNoteLead:
      'الأحدث أوّلاً، ومجموع صفحةٍ واحدة — أو صفحةٍ مُرشَّحة — ليس مجموع الحصيلة أعلاه. ولقراءة الحساب نفسه بأدواره وحالاته وسجلّ بلاغاته، افتح',

    /**
     * What the state column is, and what the row menu beside it does.
     *
     * The sentence used to end «لمنح استحقاقٍ أو رفعه، افتح صفحة المستخدم» — it
     * sent an operator away to *lift* a grant, which that surface could not do
     * either. Ending access now happens here; opening a **new** entitlement
     * still happens on the account, because that is where the account is.
     */
    stateNoteLead:
      'الحالة تتبع دورة الاستحقاق وحدها — تفعيلٌ ثمّ انتهاء عند نهاية الفترة المشتراة، ولا تجديد تلقائي — ولا تُكتب يدوياً. وما يقع من قائمة الصفّ إنهاءٌ لا تحرير: لا يُحذف سجلّ، وإنّما تُغلق مدّة الاستحقاق. ولمنح استحقاقٍ جديد، افتح صفحة المستخدم من',

    /** One destination, named once: both notes send the operator to the same register. */
    usersLink: 'قائمة المستخدمين',

    /**
     * The traversal control's own strings. The row count agrees with what it
     * counts: Arabic has a singular, a dual, a paucal for three to ten and a
     * different form beyond it, and one suffix serves none of the other three.
     */
    pager: {
      label: 'صفحات الاشتراكات',
      shownOne: 'اشتراك واحد معروض',
      shownTwo: 'اشتراكان معروضان',
      shownFew: 'اشتراكات معروضة',
      shownMany: 'اشتراكاً معروضاً',
    },
  },

  states: {
    loadingTotals: 'جارٍ تحميل الحصيلة',
    loadingLedger: 'جارٍ تحميل الاشتراكات',
    emptyTotals: 'لا إيراد محصّل بعد.',
    emptyLedger: 'لا اشتراك ولا منحة بعد.',

    /**
     * Three empties, because three different things happened.
     *
     * «There is nothing yet» sends an operator to look at the plans. «Nothing
     * matches this narrowing» sends them to widen it. «Nobody is called that»
     * sends them to the account register. One sentence for all three would be
     * wrong twice.
     */
    emptyFiltered: 'لا اشتراك ولا منحة تطابق هذا التضييق.',
    emptyFilteredAction: 'امسح المرشّحات',
    emptyMatched: 'الحسابات المطابِقة لا تملك اشتراكاً ولا منحة.',
  },
} as const;
